import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export function PayrollReport() {
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [period, setPeriod] = useState(""); // YYYY-MM
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const companyId = localStorage.getItem("demoCompanyId");

  const loadReport = async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (period) {
        params.period = period;
      } else if (year) {
        params.year = parseInt(year, 10);
      }
      const res = await apiClient.get(`/companies/${companyId}/payroll-report`, { params });
      setReport(res.data);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Laden des Lohnreports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await loadReport();
  };

  const formatAmount = (value) => {
    if (value == null) return "0.00";
    return Number(value).toFixed(2);
  };

  return (
    <div className="space-y-6" data-testid="payroll-report-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Lohnreport</h1>
        <p className="mt-1 text-sm text-slate-500">
          Übersicht der Lohnläufe nach Periode oder Jahr mit Gesamtkosten pro Mitarbeitenden.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      <Card className="p-4 max-w-xl border border-slate-200 bg-white shadow-sm" data-testid="payroll-report-form-card">
        <form className="flex flex-wrap items-end gap-3" onSubmit={handleSubmit} data-testid="payroll-report-form">
          <div className="space-y-1">
            <Label htmlFor="payroll-report-year">Jahr</Label>
            <Input
              id="payroll-report-year"
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              data-testid="payroll-report-year-input"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="payroll-report-period">Periode (optional, YYYY-MM)</Label>
            <Input
              id="payroll-report-period"
              placeholder="z.B. 2025-01"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              data-testid="payroll-report-period-input"
            />
          </div>
          <div className="pb-1">
            <Button
              type="submit"
              size="sm"
              className="rounded-full bg-slate-900 text-slate-50 px-4"
              disabled={loading}
              data-testid="payroll-report-refresh-button"
            >
              {loading ? "Lädt ..." : "Aktualisieren"}
            </Button>
          </div>
          <div className="pb-1 ml-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              data-testid="payroll-report-print-button"
            >
              Drucken / PDF
            </Button>
          </div>
        </form>
      </Card>

      {report && (
        <div className="space-y-4">
          <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="payroll-report-summary-card">
            <h2 className="text-sm font-medium text-slate-800 mb-3">Zusammenfassung</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between">
                <span>Bruttolöhne gesamt</span>
                <span className="font-mono">{formatAmount(report.summary.gross_total)}</span>
              </div>
              <div className="flex justify-between">
                <span>AN-Abzüge gesamt</span>
                <span className="font-mono">{formatAmount(report.summary.deductions_total)}</span>
              </div>
              <div className="flex justify-between">
                <span>Nettolöhne gesamt</span>
                <span className="font-mono">{formatAmount(report.summary.net_total)}</span>
              </div>
              <div className="flex justify-between">
                <span>AG-Kosten gesamt</span>
                <span className="font-mono">{formatAmount(report.summary.employer_costs_total)}</span>
              </div>
              <div className="flex justify-between">
                <span>Gesamtkosten gesamt</span>
                <span className="font-mono">{formatAmount(report.summary.total_company_cost_total)}</span>
              </div>
            </div>
          </Card>

          <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="payroll-report-table-card">
            <h2 className="text-sm font-medium text-slate-800 mb-3">Lohnläufe</h2>
            <Table data-testid="payroll-report-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Periode</TableHead>
                  <TableHead>Mitarbeiter</TableHead>
                  <TableHead>Brutto</TableHead>
                  <TableHead>AN-Abzüge</TableHead>
                  <TableHead>Netto</TableHead>
                  <TableHead>AG-Kosten</TableHead>
                  <TableHead>Gesamtkosten</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-xs text-slate-400">
                      Keine Lohnläufe gefunden.
                    </TableCell>
                  </TableRow>
                )}
                {report.entries.map((entry) => (
                  <TableRow key={`${entry.period}-${entry.employee_id}`}>
                    <TableCell>{entry.period}</TableCell>
                    <TableCell>{entry.employee_name}</TableCell>
                    <TableCell className="font-mono">{formatAmount(entry.gross_salary)}</TableCell>
                    <TableCell className="font-mono">{formatAmount(entry.employee_deductions)}</TableCell>
                    <TableCell className="font-mono">{formatAmount(entry.net_salary)}</TableCell>
                    <TableCell className="font-mono">{formatAmount(entry.employer_costs)}</TableCell>
                    <TableCell className="font-mono">{formatAmount(entry.total_company_cost)}</TableCell>
                    <TableCell>{entry.posted ? "Gebucht" : "Offen"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}
    </div>
  );
}
